"use client"

import React, { useState } from "react"
import { ChevronDown, X } from "lucide-react"
import { Trans } from 'react-i18next';
import { isValidPhoneNumber } from 'libphonenumber-js'
import styles from "./phone-input.module.css"

const countries = [
    { code: "CM", name: "Cameroon", flag: "", dialCode: "+237", length: 10  },
    { code: "CI", name: "Ivory Coast", flag: "", dialCode: "+225", length: 12 },
    { code: "GH", name: "Ghana", flag: "", dialCode: "+233", length: 9 }, 
    { code: "UG", name: "Uganda", flag: "", dialCode: "+256", length: 9 },
];

export function PhoneInput({ value = "", onChange, onDialCodeChange, onValidityChange, className = "" }) {
    const [selectedCountry, setSelectedCountry] = useState(countries[0])
    const [phoneNumber, setPhoneNumber] = useState(value)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const formatPhoneNumber = (input, country) => {
        // Remove all non-numeric characters
        const numbers = input.replace(/\D/g, "")

        // Dynamic formatting based on country length
        if (country.length <= 9) {
            // Format as XX XXX XXXX for shorter numbers
            if (numbers.length <= 2) {
                return numbers
            } else if (numbers.length <= 5) {
                return `${numbers.slice(0, 2)} ${numbers.slice(2)}`
            } else {
                return `${numbers.slice(0, 2)} ${numbers.slice(2, 5)} ${numbers.slice(5)}`
            }
        } else {
            // Format as XXX XXX XXXX for longer numbers
            if (numbers.length <= 3) {
                return numbers
            } else if (numbers.length <= 6) {
                return `${numbers.slice(0, 3)} ${numbers.slice(3)}`
            } else {
                return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6)}`
            }
        }
    }

    const generatePlaceholder = (country) => {
        if (country.length <= 9) {
            return "00 000 0000"
        } else if (country.length === 10) {
            return "000 000 0000"
        } else {
            return "000 000 00000"
        }
    }

    const validatePhoneNumber = (number, country) => {
        try {
            const fullNumber = country.dialCode + " " + number.replace(/\s/g, "")
            return isValidPhoneNumber(fullNumber, country.code)
        } catch (error) {
            return false
        }
    }

    const handlePhoneChange = (newValue) => {
        // Only allow numeric characters
        const numbersOnly = newValue.replace(/\D/g, "")

        if(!numbersOnly)
            return;

        if (numbersOnly.length <= selectedCountry.length) {
            const formattedNumber = formatPhoneNumber(numbersOnly, selectedCountry)
            setPhoneNumber(formattedNumber)
            onChange?.(selectedCountry.dialCode + " " + formattedNumber)
            onDialCodeChange?.(selectedCountry.dialCode)
            
            // Validate using libphonenumber-js
            const isValid = validatePhoneNumber(formattedNumber, selectedCountry)
            onValidityChange?.(isValid)
        }
    }

    const handleCountryChange = (country) => {
        handleClear();
        setSelectedCountry(country)
        setIsDropdownOpen(false)

        const numbersOnly = phoneNumber.replace(/\D/g, "")
        const truncatedNumbers = numbersOnly.slice(0, country.length)
        const reformattedNumber = formatPhoneNumber(truncatedNumbers, country)
        setPhoneNumber(reformattedNumber)

        onChange?.(country.dialCode + " " + reformattedNumber)
        onDialCodeChange?.(country.dialCode)
        
        // Validate using libphonenumber-js
        const isValid = validatePhoneNumber(reformattedNumber, country)
        onValidityChange?.(isValid)
    }

    const handleClear = () => {
        setPhoneNumber("")
        onChange?.(selectedCountry.dialCode + " ")
        onDialCodeChange?.(selectedCountry.dialCode)
        onValidityChange?.(false)
    }

    // Call initial validity check on mount
    React.useEffect(() => {
        onDialCodeChange?.(selectedCountry.dialCode)
        const isValid = validatePhoneNumber(phoneNumber, selectedCountry)
        onValidityChange?.(isValid)
    }, [])

    return (
        <div className={`${styles["phone-input-container"]} ${className}`}>
            <div className={styles["phone-input-wrapper"]}>
                <label htmlFor="phone" className={`${styles["phone-input-label"]} label-floating`}>
                    <Trans i18nKey="phoneNumber" />
                </label>

                <div style={{ position: "relative" }}>
                    <button type="button" className={styles["country-selector"]} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                        <span className={styles["country-flag"]}>{selectedCountry.flag}</span>
                        <ChevronDown className={styles["chevron-icon"]} />
                    </button>

                    <div className={`${styles["dropdown-menu"]} ${isDropdownOpen ? styles["dropdown-visible"] : styles["dropdown-hidden"]}`}>
                        {countries.map((country) => (
                            <div key={country.code} onClick={() => handleCountryChange(country)} className={styles["dropdown-item"]}>
                                <span className={styles["country-flag"]}>{country.flag}</span>
                                <span className={styles["dropdown-item-name"]}>{country.name}</span>
                                <span className={styles["dropdown-item-code"]}>{country.dialCode}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles["phone-input-field"]}>
                    <span className={styles["dial-code"]}>{selectedCountry.dialCode}</span>
                    <input
                        id="phone"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder={generatePlaceholder(selectedCountry)}
                        className={styles["phone-number-input"]}
                        inputMode="numeric"
                        pattern="[0-9\s]*"
                    />
                </div>

                {phoneNumber && (
                    <button type="button" onClick={handleClear} className={styles["clear-button"]}>
                        <X className={styles["clear-icon"]} />
                    </button>
                )}
            </div>
        </div>
    )
}
